import { addMonths, parse, toDate } from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceArea,
    ReferenceLine,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { IGraphPoint } from '../../lib/interfaces'
import { dateFormat, formatText } from '../../lib/dateFunctions'
import { IReportData } from './types'
import './style.less'

function getGraphHeight() {
    return 300
}

const months = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
}

export const getTicks = (graphData: { date: string }[]) => {
    if (graphData.length === 0) return []
    const startVals = graphData[0].date.split('/')
    const start = new Date(
        Number(startVals[2]),
        months[startVals[0].toLowerCase()],
        Number(startVals[1])
    )
    const endVals = graphData[graphData.length - 1].date.split('/')
    const end = new Date(Number(endVals[2]), months[endVals[0].toLowerCase()], Number(endVals[1]))
    let currentMonth = toDate(start)
    if (currentMonth.getDate() !== 1) {
        currentMonth = toDate(addMonths(toDate(start), 1))
        currentMonth.setDate(1)
    }
    let ticks = []
    while (end >= currentMonth) {
        ticks.push(formatText(currentMonth, dateFormat))
        currentMonth = addMonths(currentMonth, 1)
    }
    return ticks
}

export default function ComplianceGraph({
    data,
    visibleInterval,
    scrollToDate,
}: {
    data: IReportData
    visibleInterval: (number | Date)[]
    scrollToDate: (d: Date) => void
}) {
    const [graphWidth, setGraphWidth] = useState<number>(0)
    const [graphHeight] = useState(getGraphHeight())
    const [graphData, setGraphData] = useState<IGraphPoint[]>([])
    const [graphTicks, setGraphTicks] = useState<string[]>(getTicks(graphData))
    const [maxPoint, setMaxPoint] = useState<number>(60)

    const parentRef = useRef(null)

    useEffect(() => {
        if (data?.dates) {
            const graphDataMap: IGraphPoint[] = data?.dates.map((d) => {
                return {
                    wasindk: d.wasindk,
                    generel_daycount: null,
                    ex_period_daycount: d.daycount,
                    date: formatText(toDate(new Date(d.date)), dateFormat),
                    trueDate: new Date(d.date),
                }
            })
            setGraphData(graphDataMap)
        }
    }, [data])

    useEffect(() => {
        setGraphTicks(getTicks(graphData))
    }, [graphData])

    useEffect(() => {
        if (parentRef.current) {
            setGraphWidth((parentRef.current || { offsetWidth: 0 }).offsetWidth || 0)
        }
    }, [parentRef])

    useEffect(() => {
        const heights = graphData.map((p: IGraphPoint) => {
            return p.ex_period_daycount + 20
        })
        setMaxPoint(Math.max(...heights, 60))
    }, [graphData])

    const renderTooltipValue = (value: string | number | (string | number)[], name: string) => {
        type dkDictType = Record<number, string>
        const dkDict: dkDictType = {
            0: 'No',
            1: 'Yes',
        }
        switch (name) {
            case 'wasindk':
                if (!(typeof value === 'number')) return ['No', 'DK']
                return [dkDict[value] || 'No', 'DK']
            case 'ex_period_daycount':
                return [value, 'Exemption Period Daycount']
            case 'user_note_red':
                return ['red', 'Note']
            case 'user_note_yellow':
                return ['yellow', 'Note']
            case 'user_note_purple':
                return ['purple', 'Note']
        }
        return [value, name]
    }

    function clickGraph(e) {
        if (!e?.activeLabel) return
        scrollToDate(parse(e.activeLabel, dateFormat, new Date()))
    }

    return (
        <div ref={parentRef} className="chart-module">
            <div className="row chart-container">
                <AreaChart
                    className="graph"
                    width={graphWidth}
                    height={graphHeight}
                    data={graphData}
                    margin={{
                        top: 10,
                        right: 30,
                        left: 0,
                        bottom: 0,
                    }}
                    onClick={clickGraph}
                >
                    <defs>
                        <linearGradient id="colorBlue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#14143C" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#14143C" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#7EAC55" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#7EAC55" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        ticks={graphTicks}
                        key="date"
                        dataKey="date"
                        tickFormatter={(value) => {
                            return value.split('/')[0] + ' ' + value.split('/')[2]
                        }}
                    />
                    <YAxis allowDataOverflow={true} domain={[0, maxPoint]} />
                    <ReferenceLine y={42} stroke="red" strokeWidth={2} />
                    {/* {llPeriodLines} */}
                    <Tooltip formatter={renderTooltipValue} />
                    <Area
                        type="monotone"
                        key="ex_period_daycount"
                        dataKey="ex_period_daycount"
                        stroke="#7EAC55"
                        fill="url(#colorGreen)"
                    />
                    <Area
                        type="monotone"
                        key="wasindk"
                        dataKey="wasindk"
                        stroke="#8884d8"
                        fill="#8884d8"
                    />
                    <Area
                        type="monotone"
                        key="user_note_red"
                        dataKey="user_note_red"
                        stroke="#f5222d"
                        fill="#f5222d"
                        dot
                    />
                    <Area
                        type="monotone"
                        key="user_note_yellow"
                        dataKey="user_note_yellow"
                        stroke="#ffc53d"
                        fill="#ffc53d"
                        dot
                    />
                    <Area
                        type="monotone"
                        key="user_note_purple"
                        dataKey="user_note_purple"
                        stroke="#531dab"
                        fill="#531dab"
                        dot
                    />
                    {visibleInterval?.length > 1 && [
                        <ReferenceLine
                            x={formatText(toDate(new Date(visibleInterval[0])), dateFormat)}
                            stroke="green" //label="Period Start"
                            key={formatText(toDate(new Date(visibleInterval[0])), dateFormat)}
                        />,
                        <ReferenceLine
                            x={formatText(toDate(new Date(visibleInterval[1])), dateFormat)}
                            stroke="green" //label="Period Start"
                            key={formatText(toDate(new Date(visibleInterval[1])), dateFormat)}
                        />,
                        <ReferenceArea
                            key={'referenceline-visible-rows'}
                            x1={formatText(toDate(new Date(visibleInterval[0])), dateFormat)}
                            x2={formatText(toDate(new Date(visibleInterval[1])), dateFormat)}
                            fill="#336666"
                            // label={<RenderLabel
                            //     text={
                            //         p.period_title
                            //         || 'Untitled Exemption Period ' + idx
                            //     }
                            // />}
                            fillOpacity={0.2}
                        />,
                    ]}
                </AreaChart>
            </div>
        </div>
    )
}
